import { type TFunction } from 'i18next'

import {
  type FiveSegment,
  fiveSegmentLabelI18nKeyMap,
  fiveSegments,
  formatNineSegment,
  type NineSegment,
  nineSegments,
} from '~/utils'

// Heading of the appended block. Verbatim contract with the already-built
// analyzer, which parses this block to apply per-item sample-size thresholds.
const SAMPLE_THRESHOLD_HEADING =
  '# サンプル数しきい（各セグメント母数から算出。下回るアイテムは出力時に注記/除外）'

export type SegmentSampleThreshold = {
  label: string
  n: number
  p30: number
  p50: number
}

export const computeSegmentSampleThresholds = (
  segments: { label: string; n: number }[],
): SegmentSampleThreshold[] =>
  segments.map((s) => ({
    label: s.label,
    n: s.n,
    p30: 30 / s.n,
    p50: 50 / s.n,
  }))

// Collapse any newline a label formatter may emit (e.g. `積極\n認知未購入`) into a
// single space so each block line stays single-line.
const toSingleLineLabel = (label: string): string =>
  label.replace(/\s*\n\s*/g, ' ')

const formatPercent = (ratio: number): string => `${(ratio * 100).toFixed(1)}%`

/**
 * Appends a per-segment sample-size threshold block to a serialized table.
 *
 * Removes nothing: the serialized table is returned fully intact, followed by a
 * blank line and the frozen-format JP block. Segments with non-finite or ≤0 N
 * are skipped; if no valid segment remains the input is returned unchanged.
 *
 * @param serialized - the already-serialized table text.
 * @param segments - real segments with `{ label, n }` (label = canonical site label).
 * @returns the serialized text with the threshold block appended.
 */
export const appendSampleThresholdBlock = (
  serialized: string,
  segments: { label: string; n: number }[],
): string => {
  const thresholds = computeSegmentSampleThresholds(
    segments.filter((s) => Number.isFinite(s.n) && 0 < s.n),
  )

  // Degenerate omission: nothing valid to compute thresholds from, so emit the
  // table alone rather than a heading with no rows.
  if (thresholds.length === 0) {
    return serialized
  }

  const lines = thresholds.map(
    (threshold) =>
      `${toSingleLineLabel(threshold.label)}: 30件=${formatPercent(threshold.p30)} / 50件=${formatPercent(threshold.p50)}`,
  )
  const block = [SAMPLE_THRESHOLD_HEADING, ...lines].join('\n')

  return `${serialized}\n\n${block}`
}

// Canonical-label threshold inputs for the nine real segments. Labels come from
// the SAME formatter the 9seg columns derive their text from (intention +
// attribute, single space), so the block matches on-screen segment names.
export const nineSegmentThresholdInputs = (
  counts: Record<NineSegment, number>,
  t: TFunction,
): { label: string; n: number }[] =>
  nineSegments.map((seg) => ({
    label: formatNineSegment(seg, '%t1', t),
    n: counts[seg],
  }))

// Canonical-label threshold inputs for the five real segments. Labels come from
// the SAME i18n map the 5seg columns use.
export const fiveSegmentThresholdInputs = (
  counts: Record<FiveSegment, number>,
  t: TFunction,
): { label: string; n: number }[] =>
  fiveSegments.map((seg) => ({
    label: t(fiveSegmentLabelI18nKeyMap[seg]),
    n: counts[seg],
  }))
